import { BadgeElement, BadgeElementType, BadgeTemplate } from "sharedTypes/badge"
import { getRotatedBoundsClamp } from "components/CanvasEditor/lib/transform"
import { getElementAABB } from "components/CanvasEditor/lib/geometry"
import { getQRCodeMinSize, getTemplateDimensions } from "./badgeHelpers"
import { canLeaveBadge } from "./constants"

const round = (value: number) => Number(value.toFixed(3))
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

const rotationOf = (element: BadgeElement) => element.transforms?.rotate || 0

const minSizeOf = (element: BadgeElement) => (
  element.type === BadgeElementType.QR_CODE ? getQRCodeMinSize(element.data.format) : 0
)

const fits = (element: BadgeElement, width: number, height: number) => {
  const box = getElementAABB(element, rotationOf(element))

  return box.width <= width + 1e-6 && box.height <= height + 1e-6
}

const moveInside = (element: BadgeElement, width: number, height: number) => {
  const range = getRotatedBoundsClamp(
    element.width, element.height, rotationOf(element), width, height,
  )
  const x = round(clamp(element.x, range.minX, Math.max(range.minX, range.maxX)))
  const y = round(clamp(element.y, range.minY, Math.max(range.minY, range.maxY)))

  return x === element.x && y === element.y ? element : { ...element, x, y }
}

// A QR code is never smaller than the size it scans at
const growToMinimum = (element: BadgeElement) => {
  const minSize = minSizeOf(element)
  if (element.width >= minSize && element.height >= minSize) return element

  const size = Math.max(element.width, element.height, minSize)

  return { ...element, width: size, height: size }
}

// Shrunk to fit, keeping its proportions, never below its minimum
const shrinkToFit = (element: BadgeElement, width: number, height: number) => {
  const box = getElementAABB(element, rotationOf(element))
  const fit = Math.max(
    Math.min(1, width / box.width, height / box.height),
    minSizeOf(element) / Math.min(element.width, element.height),
  )

  return fit < 1
    ? { ...element, width: round(element.width * fit), height: round(element.height * fit) }
    : element
}

// Moved, resized, turned, or its content replaced (a QR code's format); a reorder only copies it
const changed = (element: BadgeElement, previous: BadgeElement) => (
  element.x !== previous.x || element.y !== previous.y
  || element.width !== previous.width || element.height !== previous.height
  || rotationOf(element) !== rotationOf(previous)
  || element.data !== previous.data
)

type Kept = { element: BadgeElement; undone: boolean }

const keep = (
  element: BadgeElement,
  width: number,
  height: number,
  previous?: BadgeElement,
): Kept => {
  if (canLeaveBadge(element.type)) return { element, undone: false }

  const sized = growToMinimum(element)
  if (fits(sized, width, height)) {
    return { element: moveInside(sized, width, height), undone: false }
  }

  // Its place, size and rotation go back; if that still isn't enough (a QR code's new format
  // needs more room), the whole change goes back
  if (previous) {
    const restored = growToMinimum({
      ...element,
      x: previous.x,
      y: previous.y,
      width: previous.width,
      height: previous.height,
      transforms: previous.transforms,
    } as BadgeElement)

    return {
      element: fits(restored, width, height) ? moveInside(restored, width, height) : previous,
      undone: true,
    }
  }

  return { element: moveInside(shrinkToFit(sized, width, height), width, height), undone: false }
}

/**
 * The element kept entirely on a badge of `width` × `height`, rotation included, a QR code at
 * least at the size it scans at. A change that leaves it unable to fit is undone (it goes back to
 * `previous`); with nothing to go back to, it's shrunk to fit. Then it's moved inside. Shapes,
 * lines and images may go past the edge (bleed) and are left as they are.
 */
export const keepElementOnBadge = (
  element: BadgeElement,
  width: number,
  height: number,
  previous?: BadgeElement,
) => keep(element, width, height, previous).element

/**
 * The next template with the elements it changed kept on the badge, and all of them when the
 * badge's size changed; the others stay where they are. When one changed element has to be undone,
 * the others changed with it are undone too, so a group moves, turns or resizes as a whole or not
 * at all.
 */
export const keepChangesOnBadge = (
  next: BadgeTemplate,
  previous: BadgeTemplate,
  onUndone?: () => void,
) => {
  const { width, height } = getTemplateDimensions(next)
  const before = getTemplateDimensions(previous)
  const resized = width !== before.width || height !== before.height
  const previousById = new Map(previous.badgeData.elements.map((element) => [element.id, element]))

  const results = next.badgeData.elements.map((element) => {
    const old = previousById.get(element.id)
    const touched = resized || !old || changed(element, old)

    return touched ? keep(element, width, height, resized ? undefined : old) : null
  })

  const groupUndone = results.some((result) => result?.undone)
  if (groupUndone) onUndone?.()
  const elements = next.badgeData.elements.map((element, index) => {
    const result = results[index]
    if (!result) return element

    const old = previousById.get(element.id)

    return groupUndone && old ? old : result.element
  })

  const same = elements.every((element, index) => element === next.badgeData.elements[index])

  return same ? next : { ...next, badgeData: { ...next.badgeData, elements } }
}
